import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { EMPTY_FILTER_ID } from "constants/app.constants";
import { Point } from "geojson";
import { SyncWells, setSyncWells } from "store/features";
import { RootState } from "store/rootReducer";

import { getWellsFromFilterIdPaginated } from "api/filter";

import { useGetCategoryList } from "components/groupBy/hooks/useGetCategoryList";
import { useWellList } from "components/well-list/context";

import { toDateTime } from "../util";
import { useCancelSync } from "./useCancelSync";
import {
  ClientSyncOptionModel,
  ClientSyncRequestPayload,
  ClientSyncSseEventPayload,
  ClientSyncStatusResponse,
  useClientSyncController
} from "./useClientSyncController";
import {
  PerformanceTriggerSyncType,
  usePerformanceCalculation
} from "./usePerformanceCalculation";
import { useSchemaStatus } from "./useSchemaStatus";

// ─── Types ────────────────────────────────────────────────────────────────────

export type SyncType = ClientSyncRequestPayload["syncType"];
export type SyncLoadingKey = "column" | "fcst" | "pden" | "performance" | "superSync";
export type MessageKind = "success" | "error" | "info" | "warn";

export interface PopoverMessage {
  readonly text: string;
  readonly kind: MessageKind;
}

export interface SyncTimestampStatus {
  readonly lastUpdated: string | null;
}

export interface ClientSyncViewModelProps {
  defaultJobs?: ClientSyncOptionModel[];
  currentSchema?: string;
}

export interface ClientSyncViewModel {
  // ── Schema / job ──────────────────────────────────────────────────────────
  schemas: ClientSyncOptionModel[];
  currentSchema: string;
  isLoadingSchemas: boolean;
  isSchemaDisabled: boolean;
  isJobLocked: boolean;
  isSyncServiceUnavailable: boolean;
  syncServiceErrorMessage: string;
  handleSchemaChange: (value: string) => void;
  setIsJobLocked: (locked: boolean) => void;
  refetchSchemas: () => Promise<unknown>;

  // ── Sync toggles ──────────────────────────────────────────────────────────
  isSynced: boolean;
  updateSyncState: () => void;
  useSuperSync: boolean;
  setUseSuperSync: (enabled: boolean) => void;
  useFcstFromSyncDb: boolean;
  setUseFcstFromSyncDb: (value: boolean) => void;
  usePdenFromSyncDb: boolean;
  setUsePdenFromSyncDb: (value: boolean) => void;
  forecastRescat: string;
  setForecastRescat: (rescat: string) => void;

  // ── Loading ───────────────────────────────────────────────────────────────
  loadingStatus: Readonly<Record<SyncLoadingKey, boolean>>;

  // ── Sync timestamps ───────────────────────────────────────────────────────
  columnStatus: SyncTimestampStatus;
  fcstStatus: SyncTimestampStatus;
  pdenStatus: SyncTimestampStatus;
  performanceStatus: SyncTimestampStatus;

  // ── Messages ──────────────────────────────────────────────────────────────
  syncMessages: Partial<Record<SyncType, PopoverMessage | null>>;
  globalMessage: PopoverMessage | null;
  /** Feedback message scoped to the performance calculation row in the UI. */
  performanceMessage: PopoverMessage | null;
  dismissSyncMessage: (syncType: SyncType) => void;
  dismissGlobalMessage: () => void;
  dismissPerformanceMessage: () => void;

  // ── Actions ───────────────────────────────────────────────────────────────
  startSync: (syncType: SyncType) => void;
  cancelSync: (syncType: SyncType) => void;

  // ── Hover ─────────────────────────────────────────────────────────────────
  hoveredSyncType: SyncType | null;
  setHoveredSyncType: (syncType: SyncType | null) => void;

  // ── Utilities ─────────────────────────────────────────────────────────────
  toDateTime: (date: string) => string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const AUTO_CLEAR_DELAY_MS = 10_000;
const X_MILES = 5;
const DEFAULT_FORECAST_RESCAT = "2111";
// Mirrors eva-client-sync's 10,000-UWI transport ceiling.
const MAX_SUPER_SYNC_WELLS = 10_000;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * The wells currently on the map, for the given filter, together with the
 * filter's authoritative total well count. Requesting one more than the sync
 * limit returns every well for an allowed sync without downloading an
 * arbitrarily large over-limit filter.
 *
 * Supplying pagination also makes data-svc-rs return computation failures as
 * errors instead of the empty successful response used by its unpaginated
 * route. This deliberately lets those failures propagate to the caller.
 */
const fetchWellsInFilter = async (
  filterId: string
): Promise<{ uwis: string[]; totalCount: number }> => {
  if (!filterId || filterId === EMPTY_FILTER_ID) return { uwis: [], totalCount: 0 };
  const response = await getWellsFromFilterIdPaginated(
    filterId,
    0,
    MAX_SUPER_SYNC_WELLS + 1
  );
  const uwis = (response?.data?.uwiList ?? []).map((well) => well.uniqueId);
  return { uwis, totalCount: response?.data?.totalCount ?? uwis.length };
};

const getSyncTypeDisplayName = (syncType: SyncType): string => {
  switch (syncType) {
    case "production":
      return "production";
    case "forecast":
      return "forecast";
    case "columns":
      return "columns";
  }
};

const getSyncSuccessMessage = (
  syncType: SyncType,
  payload?: ClientSyncSseEventPayload | ClientSyncStatusResponse
): string => {
  const { recordsSynced } = payload ?? {};
  if (typeof recordsSynced === "number") {
    return `${getSyncTypeDisplayName(
      syncType
    )} synced successfully (${recordsSynced} records).`;
  }
  return `${getSyncTypeDisplayName(syncType)} synced successfully.`;
};

const syncTypeToLoadingKey = (syncType: SyncType): SyncLoadingKey => {
  switch (syncType) {
    case "production":
      return "pden";
    case "forecast":
      return "fcst";
    case "columns":
      return "column";
  }
};

const buildEmptyLoadingStatus = (): Record<SyncLoadingKey, boolean> => ({
  column: false,
  fcst: false,
  pden: false,
  performance: false,
  superSync: false
});

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useClientSyncViewModel = (
  props: ClientSyncViewModelProps
): ClientSyncViewModel => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const jwtToken = useSelector((state: RootState) => state.auth.jwtToken);
  // The map's current filter id is the live source of what's on the map, and is
  // used by startSuperSync to fetch the full well list at press time. (The
  // well-list context below is only populated when the well-list panel is
  // mounted, and only ever holds its first page — so it goes stale / truncated
  // and is not used for super sync.)
  const filterId = useSelector((state: RootState) => state.filter.filterId?.id);
  // Feeds `activeWells` for single-well chart/map highlighting on schema change.
  const [wellListState] = useWellList();

  // ── Domain state ───────────────────────────────────────────────────────────

  const [currentSchema, setCurrentSchema] = useState(props.currentSchema ?? "");
  const [isJobLocked, setIsJobLocked] = useState(false);
  const [isSynced, setSyncState] = useState(false);
  const [useFcstFromSyncDb, setUseFcstFromSyncDb] = useState(true);
  const [usePdenFromSyncDb, setUsePdenFromSyncDb] = useState(true);
  const [useSuperSync, setUseSuperSync] = useState(false);
  const [forecastRescat, setForecastRescat] = useState(DEFAULT_FORECAST_RESCAT);
  const [hoveredSyncType, setHoveredSyncType] = useState<SyncType | null>(null);

  // ── Loading state ──────────────────────────────────────────────────────────

  const [loadingStatus, setLoadingStatus] = useState<Record<SyncLoadingKey, boolean>>(
    buildEmptyLoadingStatus
  );

  // ── Sync timestamp state ───────────────────────────────────────────────────

  const [columnStatus, setColumnStatus] = useState<SyncTimestampStatus>({
    lastUpdated: null
  });
  const [fcstStatus, setFcstStatus] = useState<SyncTimestampStatus>({
    lastUpdated: null
  });
  const [pdenStatus, setPdenStatus] = useState<SyncTimestampStatus>({
    lastUpdated: null
  });
  const [performanceStatus, setPerformanceStatus] = useState<SyncTimestampStatus>({
    lastUpdated: null
  });

  // ── Performance pending state ──────────────────────────────────────────────

  // ── Message state ──────────────────────────────────────────────────────────

  const [syncMessages, setSyncMessages] = useState<
    Partial<Record<SyncType, PopoverMessage | null>>
  >({});
  const [globalMessage, setGlobalMessage] = useState<PopoverMessage | null>(null);
  const [performanceMessage, setPerformanceMessage] = useState<PopoverMessage | null>(
    null
  );
  const successTimersRef = useRef<
    Partial<Record<SyncType | "global" | "performance", ReturnType<typeof setTimeout>>>
  >({});
  const pendingSpacebarSuperSyncRef = useRef<ReadonlySet<"production" | "forecast">>(
    new Set()
  );
  const superSyncInFlightRef = useRef(false);

  // ── Stable refs for values read inside event handlers / SSE callbacks ──────

  const centroidRef = useRef<Point | null>(null);
  const forecastRescatRef = useRef(forecastRescat);
  const isSyncedRef = useRef(isSynced);
  const schemaRef = useRef(currentSchema);
  // ponytail: this is single-synced-well state for chart/map highlighting
  // (activeWells[0] is read as "the" synced well by consumers), NOT the
  // super-sync well set. Super sync fetches its own list at press time from
  // the current filter — do not wire this ref into that path.
  const selectedEntitiesRef = useRef<string[]>([]);
  const useFcstFromSyncDbRef = useRef(useFcstFromSyncDb);
  const usePdenFromSyncDbRef = useRef(usePdenFromSyncDb);

  // ── Ref sync effects ───────────────────────────────────────────────────────

  useEffect(() => {
    isSyncedRef.current = isSynced;
  }, [isSynced]);

  useEffect(() => {
    schemaRef.current = currentSchema;
  }, [currentSchema]);

  useEffect(() => {
    forecastRescatRef.current = forecastRescat;
  }, [forecastRescat]);

  useEffect(() => {
    useFcstFromSyncDbRef.current = useFcstFromSyncDb;
  }, [useFcstFromSyncDb]);

  useEffect(() => {
    usePdenFromSyncDbRef.current = usePdenFromSyncDb;
  }, [usePdenFromSyncDb]);

  // ── Loading helpers ────────────────────────────────────────────────────────

  const updateLoadingStatus = useCallback((key: SyncLoadingKey, value: boolean) => {
    setLoadingStatus((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Message helpers ────────────────────────────────────────────────────────

  const showSyncMessage = useCallback((syncType: SyncType, message: PopoverMessage) => {
    clearTimeout(successTimersRef.current[syncType]);
    setSyncMessages((prev) => ({ ...prev, [syncType]: message }));

    if (message.kind === "success" || message.kind === "info") {
      successTimersRef.current[syncType] = setTimeout(() => {
        setSyncMessages((prev) => ({ ...prev, [syncType]: null }));
      }, AUTO_CLEAR_DELAY_MS);
    }
  }, []);

  const dismissSyncMessage = useCallback((syncType: SyncType) => {
    clearTimeout(successTimersRef.current[syncType]);
    setSyncMessages((prev) => ({ ...prev, [syncType]: null }));
  }, []);

  const showGlobalMessage = useCallback((message: PopoverMessage) => {
    // Always cancel any pending auto-clear first so a stale success/info timer
    // can never silently erase an error or warn that replaces it.
    clearTimeout(successTimersRef.current["global"]);
    setGlobalMessage(message);

    if (message.kind === "success" || message.kind === "info") {
      successTimersRef.current["global"] = setTimeout(() => {
        setGlobalMessage(null);
      }, AUTO_CLEAR_DELAY_MS);
    }
  }, []);

  const dismissGlobalMessage = useCallback(() => {
    clearTimeout(successTimersRef.current["global"]);
    setGlobalMessage(null);
  }, []);

  const showPerformanceMessage = useCallback((message: PopoverMessage) => {
    clearTimeout(successTimersRef.current["performance"]);
    setPerformanceMessage(message);

    if (message.kind === "success" || message.kind === "info") {
      successTimersRef.current["performance"] = setTimeout(() => {
        setPerformanceMessage(null);
      }, AUTO_CLEAR_DELAY_MS);
    }
  }, []);

  const dismissPerformanceMessage = useCallback(() => {
    clearTimeout(successTimersRef.current["performance"]);
    setPerformanceMessage(null);
  }, []);

  // ── Category list ──────────────────────────────────────────────────────────

  const { getCategoryListWithUnit, isReady } = useGetCategoryList();

  useEffect(() => {
    if (!isReady || !dispatch) return;
    getCategoryListWithUnit(undefined);
  }, [currentSchema, dispatch, getCategoryListWithUnit, isReady]);

  // ── Performance calculation ────────────────────────────────────────────────

  const handlePerfCalcComplete = useCallback(
    (
      _triggeringSyncTypes: readonly PerformanceTriggerSyncType[],
      schema: string,
      timestamp: string
    ) => {
      // Ignore completions that belong to a schema the user has already switched
      // away from — applying them would stamp the wrong timestamp on the current
      // schema's performance row.
      if (schema !== schemaRef.current) return;

      setPerformanceStatus({ lastUpdated: timestamp });
      showPerformanceMessage({
        text: "Performance calculation completed.",
        kind: "success"
      });
    },
    [showPerformanceMessage]
  );

  const handlePerfCalcError = useCallback(
    (
      _triggeringSyncTypes: readonly PerformanceTriggerSyncType[],
      schema: string,
      errorMessage: string
    ) => {
      // Same stale-schema guard as handlePerfCalcComplete.
      if (schema !== schemaRef.current) return;

      showPerformanceMessage({ text: errorMessage, kind: "error" });
    },
    [showPerformanceMessage]
  );

  const {
    isCalculating,
    triggerForSyncType,
    reset: resetPerformanceCalculation
  } = usePerformanceCalculation({
    jwtToken: jwtToken ?? undefined,
    onComplete: handlePerfCalcComplete,
    onError: handlePerfCalcError
  });

  // Sync the reactive isCalculating flag into the shared loadingStatus record
  // so the spacebar guard and the public interface see a single consistent
  // source of truth for all loading keys.
  useEffect(() => {
    updateLoadingStatus("performance", isCalculating);
  }, [isCalculating, updateLoadingStatus]);

  // ── Sync success / error handlers (passed to useClientSyncController) ──────

  const handleSyncSuccess = useCallback(
    (
      syncType: SyncType,
      payload?: ClientSyncSseEventPayload | ClientSyncStatusResponse
    ) => {
      if (syncType === "production") {
        setPdenStatus({ lastUpdated: payload?.timestamp ?? new Date().toISOString() });
        triggerForSyncType(schemaRef.current, syncType);
      }

      if (syncType === "forecast") {
        setFcstStatus({ lastUpdated: payload?.timestamp ?? new Date().toISOString() });
        triggerForSyncType(schemaRef.current, syncType);
      }

      if (syncType === "columns") {
        setColumnStatus({ lastUpdated: payload?.timestamp ?? new Date().toISOString() });
        if (isReady) {
          getCategoryListWithUnit(undefined);
        }
      }

      showSyncMessage(syncType, {
        text: payload?.message || getSyncSuccessMessage(syncType, payload),
        kind: "success"
      });

      if (syncType === "production" || syncType === "forecast") {
        const pendingSyncTypes = pendingSpacebarSuperSyncRef.current;

        if (pendingSyncTypes.has(syncType)) {
          const remainingSyncTypes = new Set(pendingSyncTypes);
          remainingSyncTypes.delete(syncType);
          pendingSpacebarSuperSyncRef.current = remainingSyncTypes;

          if (remainingSyncTypes.size === 0) {
            toast.success("Production and forecast synced successfully.");
          }
        }
      }

      updateLoadingStatus(syncTypeToLoadingKey(syncType), false);
    },
    [
      getCategoryListWithUnit,
      isReady,
      showSyncMessage,
      triggerForSyncType,
      updateLoadingStatus
    ]
  );

  const handleSyncError = useCallback(
    (syncType: SyncType, errorMessage: string) => {
      if (
        syncType === "production" ||
        syncType === "forecast" ||
        pendingSpacebarSuperSyncRef.current.size > 0
      ) {
        pendingSpacebarSuperSyncRef.current = new Set();
      }

      showSyncMessage(syncType, { text: errorMessage, kind: "error" });
      updateLoadingStatus(syncTypeToLoadingKey(syncType), false);
    },
    [showSyncMessage, updateLoadingStatus]
  );

  // ── Model: core sync engine ────────────────────────────────────────────────

  const {
    schemas,
    isLoadingSchemas,
    schemaError: schemasLoadError,
    startSync: startSyncFromController,
    correlationIdsByType,
    closeSyncStream,
    closeAllSyncStreams,
    refetchSchemas
  } = useClientSyncController({
    jwtToken: jwtToken ?? undefined,
    isAuthenticated: Boolean(user),
    organizationId: user?.organizationId,
    currentSchema,
    defaultSchemas: props.defaultJobs,
    onSchemaLoadSuccess: (loadedSchemas) => {
      if (loadedSchemas.length > 0 && !currentSchema) {
        const initialSchema =
          props.currentSchema &&
          loadedSchemas.some((s) => s.value === props.currentSchema)
            ? props.currentSchema
            : loadedSchemas[0].value;

        setCurrentSchema(initialSchema);
        schemaRef.current = initialSchema;
        setIsJobLocked(Boolean(initialSchema));
      }
    },
    onSyncSuccess: handleSyncSuccess,
    onSyncError: handleSyncError
  });

  // ── Model: schema status ───────────────────────────────────────────────────

  const schemaStatusQuery = useSchemaStatus({ schema: currentSchema || null });

  // Reset schema-scoped status and feedback immediately when the schema changes
  // so stale data from the previous schema is never shown while the new query
  // is in-flight or when it resolves to a 404. Also abort any in-flight
  // performance jobs so they stop driving loading state for the newly selected
  // schema.
  useEffect(() => {
    setPdenStatus({ lastUpdated: null });
    setFcstStatus({ lastUpdated: null });
    setColumnStatus({ lastUpdated: null });
    setPerformanceStatus({ lastUpdated: null });
    pendingSpacebarSuperSyncRef.current = new Set();
    dismissPerformanceMessage();
    resetPerformanceCalculation();
  }, [currentSchema, dismissPerformanceMessage, resetPerformanceCalculation]);

  // Populate timestamps from a successful schema status response.
  // This effect intentionally runs only when schemaStatusQuery.data changes —
  // the reset above already handles the schema-change and 404/error cases.
  useEffect(() => {
    const statuses = schemaStatusQuery.data?.statuses;
    if (!statuses || statuses.length === 0) return;

    for (const entry of statuses) {
      if (entry.syncType === "production") {
        setPdenStatus({ lastUpdated: entry.lastSyncedAt });
      } else if (entry.syncType === "forecast") {
        setFcstStatus({ lastUpdated: entry.lastSyncedAt });
      } else if (entry.syncType === "columns") {
        setColumnStatus({ lastUpdated: entry.lastSyncedAt });
      } else if (entry.syncType === "performance") {
        setPerformanceStatus({ lastUpdated: entry.lastSyncedAt });
      }
    }
  }, [schemaStatusQuery.data]);

  // ── Model: cancel sync ─────────────────────────────────────────────────────

  const { cancelSync: cancelSyncRequest } = useCancelSync({
    onCancelled: (syncType) => {
      updateLoadingStatus(syncTypeToLoadingKey(syncType), false);
      closeSyncStream(syncType);
      showSyncMessage(syncType, {
        text: `${getSyncTypeDisplayName(syncType)} sync cancelled.`,
        kind: "info"
      });
    },
    onAlreadyProcessing: (_syncType, message) => {
      showGlobalMessage({ text: message, kind: "warn" });
    },
    onNotFound: (syncType) => {
      // Operation already gone — silently clean up local state.
      updateLoadingStatus(syncTypeToLoadingKey(syncType), false);
      closeSyncStream(syncType);
    },
    onError: (syncType, message) => {
      showSyncMessage(syncType, { text: message, kind: "error" });
      updateLoadingStatus(syncTypeToLoadingKey(syncType), false);
      closeSyncStream(syncType);
    }
  });

  // ── Actions ────────────────────────────────────────────────────────────────

  const startSync = useCallback(
    (syncType: SyncType) => {
      if (!currentSchema) {
        showGlobalMessage({ text: "Please select a schema first.", kind: "error" });
        return;
      }

      updateLoadingStatus(syncTypeToLoadingKey(syncType), true);

      startSyncFromController({
        syncType,
        schema: currentSchema,
        filters: {},
        onStarted: () => {
          // Loading key was already set above; nothing extra needed here.
        }
      });
    },
    [currentSchema, showGlobalMessage, startSyncFromController, updateLoadingStatus]
  );

  const cancelSync = useCallback(
    (syncType: SyncType) => {
      const correlationId = correlationIdsByType[syncType];

      if (correlationId) {
        cancelSyncRequest(syncType, correlationId);
      } else {
        // No correlationId yet (request in-flight before server responded) —
        // fall back to client-only teardown.
        updateLoadingStatus(syncTypeToLoadingKey(syncType), false);
        closeSyncStream(syncType);
        showSyncMessage(syncType, {
          text: `${getSyncTypeDisplayName(syncType)} sync cancelled.`,
          kind: "info"
        });
      }
    },
    [
      cancelSyncRequest,
      closeSyncStream,
      correlationIdsByType,
      showSyncMessage,
      updateLoadingStatus
    ]
  );

  const updateSyncState = useCallback(() => {
    if (!currentSchema) {
      showGlobalMessage({
        text: "Please select a schema before enabling sync.",
        kind: "error"
      });
      return;
    }

    const nextSyncState = !isSynced;
    setSyncState(nextSyncState);

    if (!nextSyncState) {
      closeAllSyncStreams();
    }
  }, [closeAllSyncStreams, currentSchema, isSynced, showGlobalMessage]);

  const handleSchemaChange = useCallback(
    (value: string) => {
      setCurrentSchema(value);
      schemaRef.current = value;

      if (!isJobLocked && value) {
        setIsJobLocked(true);
      }

      const fallbackCentroid = centroidRef.current;
      const selectedWells = wellListState.wells.map((well) => well.id);

      selectedEntitiesRef.current = selectedWells;
      centroidRef.current = fallbackCentroid;

      dispatch(
        setSyncWells({
          activeWells: selectedWells,
          centroid: fallbackCentroid,
          showWellsWithin: false,
          xMiles: X_MILES,
          isSync: isSyncedRef.current,
          usePdenFromClient: usePdenFromSyncDbRef.current,
          forecastRescat: forecastRescatRef.current,
          useFcstFromClient: useFcstFromSyncDbRef.current,
          syncRequestOrigin: "Focus",
          schema: value
        } as SyncWells)
      );
    },
    [dispatch, isJobLocked, wellListState.wells]
  );

  const startSuperSync = useCallback(
    async (showCompletionToast = false) => {
      // loadingStatus.superSync is set below, after an await — too late to stop
      // key auto-repeat firing this again. This ref closes that window.
      if (superSyncInFlightRef.current) return;
      superSyncInFlightRef.current = true;

      try {
        updateLoadingStatus("superSync", true);

        // Reads the current filterId rather than any cached component state.
        // The paginated server route caches its computed result by filterId;
        // filter IDs are minted per applied filter and never reused.
        let uwis: string[];
        let totalCount: number;
        try {
          ({ uwis, totalCount } = await fetchWellsInFilter(filterId));
        } catch (error) {
          Sentry.withScope((scope) => {
            scope.setContext("startSuperSync", { filterId, schema: currentSchema });
            scope.setLevel("error");
            Sentry.captureException(error);
          });
          showGlobalMessage({
            text: "Could not load the well list for the current filter. Try again.",
            kind: "error"
          });
          return;
        }

        if (uwis.length === 0) {
          showGlobalMessage({
            text: "No active wells available for super sync.",
            kind: "info"
          });
          return;
        }

        // Keep super sync fast even though sync-svc accepts larger requests. Compare
        // against totalCount rather than uwis.length because the paginated response
        // deliberately includes at most MAX_SUPER_SYNC_WELLS + 1 entries.
        if (totalCount > MAX_SUPER_SYNC_WELLS) {
          showGlobalMessage({
            text: `Super sync is limited to ${MAX_SUPER_SYNC_WELLS.toLocaleString()} wells; the current filter has ${totalCount.toLocaleString()}. Narrow the filter and try again.`,
            kind: "warn"
          });
          return;
        }

        // Each promise resolves once the server has accepted the request
        // (onStarted fires) and rejects if the controller reports an error before
        // that point — giving us a real settled result to inspect.
        const results = await Promise.allSettled([
          new Promise<{ correlationId: string }>((resolve, reject) => {
            startSyncFromController({
              syncType: "production",
              schema: currentSchema,
              filters: { Uwis: uwis },
              onStarted: (correlationId) => resolve({ correlationId }),
              onError: (_syncType, message) => reject(new Error(message))
            });
          }),
          new Promise<{ correlationId: string }>((resolve, reject) => {
            startSyncFromController({
              syncType: "forecast",
              schema: currentSchema,
              filters: { Uwis: uwis },
              onStarted: (correlationId) => resolve({ correlationId }),
              onError: (_syncType, message) => reject(new Error(message))
            });
          })
        ]);

        const rejected = results.filter(
          (r): r is PromiseRejectedResult => r.status === "rejected"
        );

        if (rejected.length > 0) {
          pendingSpacebarSuperSyncRef.current = new Set();
          const firstError = rejected[0].reason;
          const errorMessage =
            firstError instanceof Error ? firstError.message : "Super sync failed.";
          showGlobalMessage({ text: errorMessage, kind: "error" });
          return;
        }

        // Only mark individual sync types as loading once we know both requests
        // were accepted by the server.
        updateLoadingStatus("pden", true);
        updateLoadingStatus("fcst", true);
        pendingSpacebarSuperSyncRef.current = showCompletionToast
          ? new Set(["production", "forecast"])
          : new Set();
        showGlobalMessage({ text: "Super sync started.", kind: "success" });
      } finally {
        superSyncInFlightRef.current = false;
        updateLoadingStatus("superSync", false);
      }
    },
    [
      currentSchema,
      filterId,
      showGlobalMessage,
      startSyncFromController,
      updateLoadingStatus
    ]
  );

  // ── Hotkeys ────────────────────────────────────────────────────────────────

  useHotkeys(
    "f",
    () => {
      startSync("forecast");
    },
    [startSync]
  );

  // ── Spacebar super sync ────────────────────────────────────────────────────

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase?.() ?? "";
      const isTyping = tagName === "input" || tagName === "textarea";
      const isSpacebar = event.key === " " || event.code === "Space";

      if (!isSpacebar || !useSuperSync || isTyping) {
        return;
      }

      event.preventDefault();

      if (loadingStatus.pden || loadingStatus.fcst || loadingStatus.performance) {
        const fieldsToUpdate: string[] = [];
        if (loadingStatus.pden) fieldsToUpdate.push("Production");
        if (loadingStatus.fcst) fieldsToUpdate.push("Forecast");
        if (loadingStatus.performance) fieldsToUpdate.push("Performance");

        showGlobalMessage({
          text: `Updating: ${fieldsToUpdate.join(" and ")}. Please try again later.`,
          kind: "info"
        });
        return;
      }

      if (!loadingStatus.superSync) {
        void startSuperSync(true);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    loadingStatus.fcst,
    loadingStatus.pden,
    loadingStatus.performance,
    loadingStatus.superSync,
    showGlobalMessage,
    startSuperSync,
    useSuperSync
  ]);

  // ── Dispatch sync state to Redux ───────────────────────────────────────────

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      dispatch(
        setSyncWells({
          activeWells: selectedEntitiesRef.current,
          isSync: isSynced,
          showWellsWithin: false,
          xMiles: X_MILES,
          usePdenFromClient: usePdenFromSyncDb,
          forecastRescat,
          centroid: centroidRef.current,
          schema: schemaRef.current,
          syncRequestOrigin: "Focus",
          useFcstFromClient: useFcstFromSyncDb
        } as SyncWells)
      );
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [
    isSynced,
    usePdenFromSyncDb,
    useFcstFromSyncDb,
    forecastRescat,
    currentSchema,
    pdenStatus,
    fcstStatus,
    performanceStatus,
    columnStatus,
    dispatch
  ]);

  // ── Cleanup streams on unmount ─────────────────────────────────────────────

  useEffect(() => {
    return () => {
      closeAllSyncStreams();
    };
  }, [closeAllSyncStreams]);

  // ── Derived UI state ───────────────────────────────────────────────────────

  const isSchemaDisabled = useMemo(
    () => isLoadingSchemas || schemas.length === 0,
    [isLoadingSchemas, schemas.length]
  );

  const isSyncServiceUnavailable = useMemo(
    () => Boolean(schemasLoadError) && !isLoadingSchemas,
    [isLoadingSchemas, schemasLoadError]
  );

  const syncServiceErrorMessage = useMemo(
    () => schemasLoadError ?? "Sync service is not available.",
    [schemasLoadError]
  );

  // ── Public interface ───────────────────────────────────────────────────────

  return {
    // Schema / job
    schemas,
    currentSchema,
    isLoadingSchemas,
    isSchemaDisabled,
    isJobLocked,
    isSyncServiceUnavailable,
    syncServiceErrorMessage,
    handleSchemaChange,
    setIsJobLocked,
    refetchSchemas,

    // Sync toggles
    isSynced,
    updateSyncState,
    useSuperSync,
    setUseSuperSync,
    useFcstFromSyncDb,
    setUseFcstFromSyncDb,
    usePdenFromSyncDb,
    setUsePdenFromSyncDb,
    forecastRescat,
    setForecastRescat,

    // Loading
    loadingStatus,

    // Timestamps
    columnStatus,
    fcstStatus,
    pdenStatus,
    performanceStatus,

    // Messages
    syncMessages,
    globalMessage,
    performanceMessage,
    dismissSyncMessage,
    dismissGlobalMessage,
    dismissPerformanceMessage,

    // Actions
    startSync,
    cancelSync,

    // Hover
    hoveredSyncType,
    setHoveredSyncType,

    // Utilities
    toDateTime
  };
};

export default useClientSyncViewModel;
